import { immer } from 'zustand/middleware/immer';
import { combine, devtools } from 'zustand/middleware';
import type {
  AccessRequestUpdateType,
  AccessRequestReadType,
  AccessRequestCreateType,
} from 'bc-model';
import apiAction, { handleApiLifecycle } from 'stores/actions/api';
import {
  createAccessRequest,
  retrieveAccessRequests,
  updateAccessRequest,
} from 'api-client/accessRequests';
import { createWithEqualityFn } from 'zustand/traditional';
import type { StoreApi } from 'zustand';

type AccessRequestStateType = {
  loaded: { [key: string]: boolean };
  loading: { [key: string]: boolean };
  failure: { [key: string]: Error };
  accessRequests: { [key: string]: AccessRequestReadType };
  createAccessRequest: (accessRequest: AccessRequestCreateType) => void;
  loadAccessRequests: (force?: boolean) => void;
  updateAccessRequest: (accessRequest: AccessRequestUpdateType) => void;
  updateAccessRequestFromEvent: (accessRequest: AccessRequestReadType) => void;
};

const unloadedState: AccessRequestStateType = {
  loaded: {},
  loading: { init: true },
  failure: {},
  accessRequests: {},
  createAccessRequest: () => {},
  loadAccessRequests: () => {},
  updateAccessRequest: () => {},
  updateAccessRequestFromEvent: () => {},
};

const storeActions = (
  set: StoreApi<AccessRequestStateType>['setState'],
  get: StoreApi<AccessRequestStateType>['getState']
) => ({
  createAccessRequest: apiAction(
    createAccessRequest,
    handleApiLifecycle(
      set,
      'accessRequests',
      (state: AccessRequestStateType, accessRequest: AccessRequestReadType) => {
        state.accessRequests[accessRequest.id] = accessRequest;
      }
    )
  ),
  updateAccessRequestFromEvent: (payload: AccessRequestReadType) => {
    set((state) => {
      state.accessRequests[payload.id] = payload;
      return state;
    });
  },
  loadAccessRequests: async (force = false) => {
    const { loaded, loading } = get();
    if (loading.accessRequests) return;
    if (!force && loaded.accessRequests) return;

    set((state) => ({
      loading: { ...state.loading, accessRequests: true },
    }));

    const fetchAndLoad = apiAction(
      retrieveAccessRequests,
      handleApiLifecycle(
        set,
        'accessRequests',
        (
          state: AccessRequestStateType,
          accessRequestMap: { [id: number]: AccessRequestReadType }
        ) => {
          state.accessRequests = accessRequestMap;
          state.loaded.accessRequests = true;
          state.loading.accessRequests = false;
        }
      )
    );
    await fetchAndLoad();
  },
  updateAccessRequest: apiAction(
    updateAccessRequest,
    handleApiLifecycle(
      set,
      'accessRequests',
      (state: AccessRequestStateType, accessRequest: AccessRequestReadType) => {
        state.accessRequests[accessRequest.id] = accessRequest;
      }
    )
  ),
});

const useAccessRequestStore = createWithEqualityFn(
  devtools(immer(combine(unloadedState, storeActions)), { name: 'accessRequestStore' })
);

export type { AccessRequestStateType };

export default useAccessRequestStore;
